<template>
  <div class="fee-settings-category">
    <v-row>
      <v-col cols="12" md="auto">
        <h1 class="text-left mx-4 mt-2">Fee Category</h1>
        <h5 class="text-left mx-4">{{activeFeeSession.title }}</h5>
      </v-col>
      <v-spacer></v-spacer>
      <v-col cols="12" md="auto">
         <v-btn color="white" 
            @click="showImportDialog = true"
            class="mx-2 my-2"
            light
          >
            Import
            <v-icon right>
              mdi-import
            </v-icon>
          </v-btn>
        <v-btn :to="{ name: 'AddFeeCategory' }" color="primary" class="mx-2 mr-4 my-2" data-cy="fee-category-add-new-button">Add Fee Category</v-btn>
      </v-col>
      
    </v-row>

    <v-row>
      <v-tabs v-model="tab">
        <v-tab class="ml-4">Categories</v-tab>
        <v-tab>Allocate Category</v-tab>
        <!-- Category tab item -->
        <v-tab-item class="mx-4">
          <v-row>
            <v-col cols="12">
              <h2 v-if="!categories.length && !isLoading" data-cy="categories-not-found">No Fee Categories Found.</h2>
            </v-col>
            <v-col cols="12" md="4" v-for="category in categories" :key="category.id">
              <v-card
                class="py-4 mx-4 my-2 px-4"
                :to="{ name: 'EditFeeCategory', params: {id: category.id}}"
              >
                <h2 class="text-left font-weight-medium mb-4">{{ category.code }}</h2>
                <p class="text--secondary text-left font-weight-medium mb-0">
                  <small>{{ category.title }}</small>
                </p>
              </v-card>
            </v-col>
          </v-row>
        </v-tab-item>

        <!-- Allocate Student tab item -->
        <v-tab-item class="mx-8 my-4">
          <!-- filter by category -->
          <v-row>
            <v-col cols="12" md="4">
              <v-select
                v-model="filter.category"
                :items="categories"
                small-chips
                outlined
                color="primary"
                label="Filter by Category"
                multiple
                hide-details
                dense
                background-color="transparent"
                item-value="code"
                item-text="code"
                class="mb-4 rounded-input"
                @change="getAllAccounts()"
              ></v-select>
            </v-col>

            <v-col cols="12" md="4">
              <v-select
                v-model="filter.room"
                :items="rooms"
                small-chips
                outlined
                color="primary"
                label="Filter by Classroom"
                multiple
                hide-details
                dense
                background-color="transparent"
                item-value="id"
                item-text="sections"
                class="mb-4 rounded-input"
                @change="getAllAccounts()"
              ></v-select>
            </v-col>
          </v-row>

          <!-- filter end -->
          <v-card>
            <v-card-title>
              Student Category Details
              <v-spacer></v-spacer>
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Search"
                single-line
                hide-details
              ></v-text-field>
            </v-card-title>
            <v-data-table
              :headers="headers"
              :items="accounts"
              :search="search"
              :options.sync="options"
              :items-per-page="15"
              :multi-sort="true"
              :server-items-length="pagination.totalCount"
            >
              <template v-slot:item.image="{item}">
                <v-avatar width="30" height="30">
                  <img :src="item.student.image" />
                </v-avatar>
              </template>
              <template v-slot:item.full_name="{item}">
                <v-btn
                  :ripple="false"
                  color="primary"
                  link
                  text
                  @click="navigateToStudentsFeeDetails(item.student)"
                >
                  <span class="font-weight-bold">{{ item.student.full_name }}</span>
                </v-btn>
              </template>
              <template v-slot:item.phone="{item}">
                <span class="font-weight-bold">{{ item.student.phone }}</span>
              </template>
              <template v-slot:item.room="{ item }">
                <v-chip color="primary" outlined>
                  <h5 v-if="item.room">{{ item.room.sections }}</h5>
                  <h5 v-else>Not Assigned</h5>
                </v-chip>
              </template>

              <template v-slot:item.category="{item}">
                <v-row justify="end">
                  <v-select
                    v-model="item.category"
                    :items="getRoomCategories(item.room)"
                    chips
                    flat
                    color="primary"
                    tile
                    solo
                    hide-details
                    :suffix="item.category? null: 'Select Category'"
                    class="selections"
                    background-color="transparent"
                    return-object
                    @change="saveStudentCategory(item)"
                    item-text="code"
                    clearable
                  >
                    <template v-slot:selection="{item,select}">
                      <v-chip color="primary" outlined @click="select">
                        <strong>{{ item.code }}</strong>
                      </v-chip>
                    </template>
                  </v-select>
                </v-row>
              </template>
            </v-data-table>
          </v-card>
        </v-tab-item>
      </v-tabs>
    </v-row>
    <v-row justify="center">
      <ConfirmationDialog
        title="Are you sure you want to change this student's Fee Category?"
        :description="`This account has a total amount of ${currencyFormat(amountNeedsToBeAdjusted)} that needs to be adjusted before changing the Fee Category`"
        successText="Proceed"
        :disableSuccessButton="!refundFormValid"
        @successCallback="refundPaidAndChangeCategory()"
        failureText="Cancel"
        @failureCallback="closeDialog"
        :visible="dialog"
      >
        <slot>
          <v-form v-model="refundFormValid" :ref="refundFormValid">
            <v-text-field
              v-model="refundReceiptNumber"
              class="rounded-input mx-5"
              outlined
              dense
              :rules="[rules.required]"
              placeholder="Receipt No."
            />
            <v-text-field
              v-model="refundRemarks"
              class="rounded-input mx-5"
              outlined
              dense
              placeholder="Remarks (Optional)"
              hint="This will be displayed in the refund receipt."
            />
          </v-form>
        </slot>
      </ConfirmationDialog>
    </v-row>

    <v-row justify="center">
      <ConfirmationDialog
        title="Are you sure you want to change this student's Fee Category to 'Not Set'?"
        :description="`This will not delete the existing receipts and will set the pending balance (if any) to zero.`"
        successText="Proceed"
        @successCallback="saveStudentCategory(resettingCategoryAccount)"
        failureText="Cancel"
        @failureCallback="closeResetCategoryDialog"
        :visible="resetCategoryDialog"
      >
      </ConfirmationDialog>
    </v-row>

     <!-- import excel dialog -->
    <ImportExcelDialog
			:showDialog="showImportDialog"
			title="Import Fee Categories"
			@closeImportDialog="showImportDialog = false"
			:endpoint="Helper.addUrlParams(endpoints.importFeeCategories, 'session=' + activeFeeSession.id)"
			:staticFile="endpoints.static.feeCategoriesImportFile"
		/>

    <v-snackbar v-model="snackbar.visible">
      <span>{{ snackbar.text }}</span>
      <template>
        <v-btn color="pink" text @click="snackbar.visible = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import api from "@api/index";
import endpoints from "@api/repository";
import ConfirmationDialog from "@components/widgets/dialogs/ConfirmationDialog";
import Helper from "@utils/misc";
import ImportExcelDialog from '@components/widgets/dialogs/ImportExcelDialog';
import TourHelper from "@utils/tour_helper";


export default {
  name: "FeeSettingsCategory",
  props: {
    searchUsername: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      refundFormValid: true,
      amountNeedsToBeAdjusted: null,
      accountNeedingReadjustment: null,
      desiredCategory: null,
      endpoints:endpoints, 
      Helper:Helper,
      // above three are used for refund and readjustment of paid amount
      options: {},
      tab:0,
      rooms: [],
      pagination: {
        totalCount: 0,
        url: endpoints.getAllFeeAccounts,
      },
      dialog: false,
      resetCategoryDialog: false,
      resettingCategoryAccount: null,
      search: "",
      filter: {
        category: null,
        room: null,
      },
      rules: {
        required: (v) => !!v || "Required",
      },
      categories: [],
      headers: [
        { text: "Photo", value: "image", align: "center", sortable: false },
        { text: "Name", align: "center", value: "full_name" },
        { text: "Phone Number", align: "center", value: "phone" },
        { text: "Classroom", value: "room", align: "center" },
        { text: "Category", value: "category", align: "center" },
      ],
      accounts: [],

      refundRemarks: null,
      refundReceiptNumber: null,

      showImportDialog: false,

      snackbar: {
        visible: false,
        text: "",
      },
    };
  },
  components: {
    ConfirmationDialog,
    ImportExcelDialog
  },
  computed: {
    ...mapGetters(["currentUser", "accessToken", "isLoading","activeFeeSession"]),
    essentials() {
      return {
        accessToken: this.accessToken,
        setLoading: this.setLoading,
        handleErrorsFunction: this.handleApiErrors,
      };
    },
  },
  methods: {
    ...mapActions(["setLoading"]),
    currencyFormat: Helper.currencyFormat,

    handleTour() {
      var steps = [];
      steps.push({id: '1', title: "Welcome to Fee Category Section!", description: "Here you can modify and allocate fee category", videoLink: "https://youtube.com"});
      // steps.push({id: '2', title: "Fee Settings", description: "Access for Fee Settings Module", el: '#fee-settings-btn',position:'left'});
      // steps.push({id: '3', title: "Fee Records Table", description: "View All Fee Records by name, phone no, class, category and status, click on a Row for more Information.", el: '#fee-records-table',position:'top'});
      TourHelper.startTour(this, "fee__category", steps);
    },

    async closeDialog() {
      this.dialog = false;
      this.amountNeedsToBeAdjusted = null;
      this.accountNeedingReadjustment = null;
      this.desiredCategory = null;
      this.refundRemarks = null;
      this.refundReceiptNumber = null;
    },

    async closeResetCategoryDialog(){
      this.resetCategoryDialog = false;
      this.desiredCategory = null;
      this.resettingCategoryAccount = null;
      this.getAllAccounts()
    },

    // queries all fee categories
    async getAllCategories() {
      var url = endpoints.getAllCategories + `?session=${this.activeFeeSession.id}`; 
      var response;
      response = await api.handleApi({
        url: url,
        apiEssentials: this.essentials,
        responseVar: response,
      });

      if (response) {
        this.categories = response;
      }
    },
    // queries all fee accounts
    async getAllAccounts() {
      var response;
      var url = Helper.addUrlParams(this.pagination.url, `session=${this.activeFeeSession.id}`)
      // var url = this.pagination.url + (this.pagination.url.split("?")[1] ? "&" : "?") + `session=${this.activeFeeSession.id}`;
      // if search query exists, add to the url as a GET param w/o disturbing other params
      if (this.search) {
        // url += (url.split("?")[1] ? "&" : "?") + "query=" + this.search;
        url = Helper.addUrlParams(url, "query=" + this.search);
      }
      if (this.filter.category) {
        url += "&category=" + this.filter.category.toString();
      }
      if (this.filter.room) {
        url += "&room=" + this.filter.room.toString();
      }

      response = await api.handleApi({
        apiEssentials: this.essentials,
        url: url,
        responseVar: response,
      });

      if (response) {
        // console.log(response);
        this.accounts = response.results;
        this.pagination.totalCount = response.count;
        this.pagination.nextUrl = response.next;
      }
    },

    containsRoom(obj, list) {
      var i;
      for (i = 0; i < list.length; i++) {
        if (list[i].id === obj.id) {
          return true;
        }
      }

      return false;
    },

    getRoomCategories(room) {
      if (!room) {
        return [];
      }
      // var response;
      // var url = endpoints.getStudentRoomCategories + room.id +"/";
      // response = await api.handleApi({
      //     apiEssentials: this.apiEssentials,
      //     url: url,
      //     responseVar: response,
      // });
      // if(response){
      //     return response;
      // }
      return this.categories.filter((category) =>
        this.containsRoom(room, category.rooms)
      );
    },

    async saveStudentCategory(account) {
      this.amountNeedsToBeAdjusted = null;
      this.accountNeedingReadjustment = null;
      this.desiredCategory = account.category;
      // console.log(account);
      var response;
      if(!this.desiredCategory && !this.resetCategoryDialog){
        this.resettingCategoryAccount = account;
        this.resetCategoryDialog = true;
        return
      }
      response = await api.handleApi({
        apiEssentials: this.essentials,
        url: endpoints.saveStudentCategory,
        responseVar: response,
        method: api.Methods.POST,
        body: account,
      });
      if (response) {
        var index = this.accounts.indexOf(account);
        if (response.is_adjusting) {
          this.accounts[index] = response.previous;
          this.amountNeedsToBeAdjusted = response.amount;
          this.accountNeedingReadjustment = response.previous;

          // account = response.previous;
          await this.getAllAccounts();
          this.dialog = true;
          return;
        }
        this.accounts[index] = response;
        this.resetCategoryDialog = false;
        this.resettingCategoryAccount = null;
      }
    },

    // gets all the money transactions made by the user and put into refund and generate a refund receipt
    async refundPaidAndChangeCategory() {
      if (!this.refundReceiptNumber) {
        this.snackbar.text = "Invalid Receipt Number";
        this.snackbar.visible = false;
        return;
      }
      var account = this.accountNeedingReadjustment;
      var category_id = this.desiredCategory.id;
      var remarks = this.refundRemarks;
      var receipt_number = this.refundReceiptNumber;
      this.closeDialog();

      var response = await api.call(
        this.essentials,
        endpoints.readjustStudentsFeesAndChangeCategory,
        api.Methods.POST,
        {
          fee_account_id: account.id,
          desired_category_id: category_id,
          remarks: remarks,
          receipt_number: receipt_number,
        }
      );

      if (response) {
        var serverAccount = this.accounts.find((a) => a.id == response.id);
        var index = this.accounts.indexOf(serverAccount);
        console.log(index);
        this.accounts[index].category = response.category;
        this.snackbar.text =
          "Refund receipt is generated in the Student's Fee Profile";
        this.snackbar.visible = true;
      }
    },

    async navigateToStudentsFeeDetails(student) {
      if (!student.room) {
        this.snackbar.text = "Room is not assigned to the user";
        this.snackbar.visible = true;
      }
      this.$router.push({
        name: "StudentFeeDetails",
        params: { sid: student.id },
      });
    },

    async getRooms() {
      var response;
      var url = endpoints.getRooms + `?session=${this.activeFeeSession.academic_session.id}`;

      response = await api.handleApi({
        apiEssentials: this.essentials,
        url: url,
        responseVar: response,
      });

      if (response instanceof Error) {
        this.setLoading(false);
        this.snackbar.text = "Error retrieving rooms";
        this.snackbar.visible = true;
        return;
      }
      this.rooms = response;
    },

    // handles api errors
    async handleApiErrors(err) {
      this.setLoading(false);
      if (err.response) {
        console.log(err.response);
      } else {
        this.snackbar.text = "Unknown error occurred";
        this.snackbar.visible = true;
      }
    },
  },

  watch: {
    options: {
      async handler() {
        // console.log("something changed");
        // console.log(this.options);
        this.pagination.url = Helper.addUrlParams(endpoints.getAllFeeAccounts, "page=" + this.options.page);
        if (this.options.sortBy.length > 0) {
          // get url var
          var params = "&o=";
          // iterate over sortBy options and add sortby and sortdesc seperated with commas
          for (var sortOption in this.options.sortBy) {
            params +=
              (this.options.sortDesc[sortOption] ? "" : "-") +
              this.options.sortBy[sortOption];
            if (sortOption != this.options.sortBy.length - 1) params += ",";
          }

          // handles two level db model ordering
          if (params.includes("full_name")) {
            params = params.replace("full_name", "student__full_name");
          }
          if (params.includes("phone")) {
            params = params.replace("phone", "student__phone");
          }
          if (params.includes("room")) {
            params = params.replace("room", "student__room");
          }
          this.pagination.url += params;
        }
        this.getAllAccounts();
      },
    },
    search: {
      async handler() {
        var searched = this.search;
        var self = this;
        setTimeout(function () {
          if (searched == self.search) {
            self.getAllAccounts();
          }
        }, 500);
      },
    },
  },

  created() {
    this.handleTour();
    this.getAllCategories();
    this.getRooms();
    // this.getAllAccounts(); <- called in watch options
    if(this.searchUsername){
      this.tab = 1
      this.search = this.searchUsername
    }
  },
};
</script>

<style scoped>
h2 {
  font-size: 24px;
}
.selections {
  max-width: min-content;
}
.v-input__slot.transparent {
  padding: 0 !important;
}
.rounded-input {
  border-radius: 12px !important;
}
</style>